import type {
  IAffärshändelse,
  IAffärshändelseRespons,
  IBokföringAvSparkontoRespons,
  IBokföringAvTransaktion,
  IManuellAffärshändelse,
  IVerifikation
} from 'src/types/nya/ekonomi/bokforing/bokforingstyper';


import type {
  IArbetsgivardeklaration,
  IKundfaktura,
  KundfakturaParsningsRespons
} from "../../types/nya/ekonomi/faktura/modell/Kundfaktura";
import { IMatchningAvLön } from "../../types/nya/ekonomi/loner/LoneTyper";
import { IRäkenskapsår } from 'src/types/nya/common';
import { IBanktransaktion } from 'src/types/nya/ekonomi/bank/BankTyper';
import { IBokföringAvSkatteverketTransaktion } from "../../types/nya/ekonomi/skatt/SkatteTyper";
import { IAiBokföringsFörfrågan, IAiBokföringsFörslagAvAffärshändelse } from "../../types/nya/ekonomi/bokforing/aibokforingstyper";
import axiosInstance, { endpoints } from '@/src/lib/axios';


export function SparaManuelltSkapadAffärshändelse(affärshändele: IManuellAffärshändelse, orgid: string): Promise<IAffärshändelseRespons> {
  return axiosInstance.post(endpoints.bokföring.affärshändelser.sparaManuell(orgid), affärshändele)
    .then(response => response.data)
    .catch(error => Promise.resolve({
      orgId: orgid,
      apiResultat: {
        meddelande: 'Ett fel uppstod under parsningen: ' + error.message,
        framgångsRikt: false,
      },
      kundfaktura: undefined
    })
    );
}

export function parsaKundfaktura(file: File, orgId: string): Promise<KundfakturaParsningsRespons> {
  const formData = new FormData();
  formData.append('kundfaktura', file);
  return axiosInstance.post(endpoints.bokföring.fil.kundfaktura + '/' + orgId, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
    .then(response => response.data)
    .catch(error => Promise.resolve({
      orgId: orgId,
      apiResultat: {
        meddelande: 'Ett fel uppstod under parsningen: ' + error.message,
        framgångsRikt: false,
      },
      kundfaktura: undefined
    })
    );
}

export function skapaAiFörslagTillBokföring(aiBokföringsFörfrågan: IAiBokföringsFörfrågan, orgId: string): Promise<IAiBokföringsFörslagAvAffärshändelse> {
  const formData = new FormData();
  aiBokföringsFörfrågan.underlag.forEach(fil => {
    formData.append('underlag', fil);
  });
  const body = {
    titel: aiBokföringsFörfrågan.titel,
    beskrivning: aiBokföringsFörfrågan.beskrivning
  };
  formData.set(
    "aiBokföringsFörfrågan",
    new Blob([JSON.stringify(body)], { type: "application/json" })
  );
  
  return axiosInstance.post(endpoints.bokföring.affärshändelser.skapaFörslagMedAi(orgId), formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
    .then(response => response.data);
}

export function bokforKundfaktura(kundfaktura: IKundfaktura, orgId: string): Promise<IAffärshändelseRespons> {
  

  const body = {
    orgId,
    kundfaktura
  };

  return axiosInstance.post(endpoints.bokföring.bokfor.kundfaktura, body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}


export function hämtaAffärshändelser(orgId: string) {
  return axiosInstance
    .get(endpoints.bokföring.affärshändelser.hämtaAllaFörOrg(orgId))
    .then(response => response.data)
    .catch(error => { console.error(error); });
}

export function hämtaAffärshändelserFörPeriod(
  orgId: string,
  förstaDag?: string,
  sistaDag?: string,
  kategori?: string
): Promise<{ orgId: string; affärshändelser: IAffärshändelseSlim[] }> {
  const params = new URLSearchParams();
  if (förstaDag) params.append('förstaDag', förstaDag);
  if (sistaDag) params.append('sistaDag', sistaDag);
  if (kategori) params.append('kategori', kategori);
  
  const queryString = params.toString();
  const url = queryString 
    ? `${endpoints.bokföring.affärshändelser.hämtaAllaFörOrg(orgId)}?${queryString}`
    : endpoints.bokföring.affärshändelser.hämtaAllaFörOrg(orgId);
    
  return axiosInstance.get(url).then(r => r.data);
}

export type IAffärshändelseSlim = {
  id: string;
  orgId: string;
  beskrivning: string;
  typ: { namn: string };
  kategori: { namn: string };
  totaltBelopp: number;
  motpart: { namn: string };
  bokföringsDatum: string;
  inträffade: string;
  status: string;
};

export type ISaldoHistorikEntry = {
  datum: string;
  belopp: { värde: number };
};

export function hämtaSaldoHistorik(
  orgId: string,
  kontoNummer: number,
  månader: number
): Promise<ISaldoHistorikEntry[]> {
  return axiosInstance
    .get(endpoints.bokföring.affärshändelser.saldoHistorik(orgId, kontoNummer, månader))
    .then(r => r.data);
}

export type IKontoDto = {
  namn: string;
  kontonummer: number;
};

export function hämtaKontoplan(
  orgId: string
): Promise<{ orgId: string; konton: IKontoDto[] }> {
  return axiosInstance
    .get(endpoints.bokföring.kontoplan(orgId))
    .then(r => r.data);
}

export type IPeriodiseringsPost = {
  affärshändelseId: string;
  beskrivning: string;
  datum: string;
  belopp: number;
  interimTyp: string;
};

export function hämtaPeriodiseringsposter(
  orgId: string,
  räkenskapsårStart: string,
  räkenskapsårSlut: string
): Promise<IPeriodiseringsPost[]> {
  return axiosInstance
    .get(endpoints.bokföring.bokslutProcess.periodisering(orgId, räkenskapsårStart, räkenskapsårSlut))
    .then(r => r.data);
}

export function hämtaAffärshändelse(orgId: string, affärshändelseId: string) {

  return axiosInstance.get(endpoints.bokföring.affärshändelser.hämta(orgId, affärshändelseId))
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}

export function SkapaUtkastTillBokföringAvLön(
  matchningAvLön: IMatchningAvLön,
  valdBanktransaktion: IBanktransaktion,
  orgId: string): Promise<IAffärshändelseRespons> {

  
  const body = {
    orgId,
    matchningAvLön: { ...matchningAvLön, valdBanktransaktion }
  };

  return axiosInstance.post(endpoints.bokföring.lön.utkast, body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}

export function BokförAllaUtkastTillBokföringAvSkattetransaktioner(
  bokforingAvSkatteverkettransaktioner: IBokföringAvSkatteverketTransaktion[],
  orgId: string,
  skallBokföras: boolean = true): Promise<{ orgId: string; affärshändelser: IAffärshändelse[]; duplikat: IAffärshändelse[] }> {


  const body = {
    orgId,
    skallBokföras,
    bokföringAvSkatteverkettransaktioner: bokforingAvSkatteverkettransaktioner
  };

  return axiosInstance.post(endpoints.bokföring.bokfor.skatt.batch, body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });

}


export function BokförAllaUtkastTillMatchningarAvLöner(
  matchningarAvLöner: IMatchningAvLön[],
  orgId: string,
  skallBokföras: boolean = true): Promise<{ orgId: string; affärshändelser: IAffärshändelse[] }> {


  const body = {
    orgId,
    skallBokföras,
    matchningarAvLöner
  };

  return axiosInstance.post(endpoints.bokföring.bokfor.loner.batch, body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
      throw error;
    });

}

export function SkapaUtkastTillBokföringAvSkattetransaktion(
  bokforingAvSkatteverkettransaktion: IBokföringAvSkatteverketTransaktion,
  valdBanktransaktion: IBanktransaktion | undefined,
  orgId: string,
  skallBokföras: boolean = true): Promise<IAffärshändelseRespons> {

  const body = {
    orgId,
    skallBokföras,
    bokföringAvSkatteverkettransaktion: {
      ...bokforingAvSkatteverkettransaktion,
      matchandeBanktransaktion: valdBanktransaktion
    }
  };

  return axiosInstance.post(endpoints.bokföring.bokfor.skatt.skattetransaktion, body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}

export function BokförVerifikationerFörLön(
  affärshändelseId: string,
  verifikationer: IVerifikation[],
  orgId: string): Promise<IAffärshändelseRespons> {

  const body = {
    orgId,
    affärshändelseId,
    verifikationer
  };



  return axiosInstance.post(endpoints.bokföring.lön.bokfor, body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}

export function SlutförBokföringAvVerifikationer(
  orgId: string,
  affärshändelseId: string,
  verifikationer: IVerifikation[]): Promise<IAffärshändelseRespons> {

  

  const body = {
    orgId,
    affärshändelseId,
    verifikationer
  };

  return axiosInstance.post(endpoints.bokföring.bokfor.verifikation.slutför, body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}

export function RaderaAffärshändelse(orgId: string,
  affärshändelseId: string) {

  return axiosInstance.delete(endpoints.bokföring.affärshändelser.radera(orgId, affärshändelseId))
    .then(response => {
      return response.data
    });

}

export function RaderaAffärshändelser(orgId: string,
  affärshändelseIdn: string[]) {

  const body = {
    affärshändelseIdn
  }
  return axiosInstance.post(endpoints.bokföring.affärshändelser.raderaBatch(orgId),
    body)
    .then(response => {
      return response.data
    });

}


export function SparaAffärshändelse(
  orgId: string,
  affärshändelse: IAffärshändelse,
  skallBokföras: boolean = true,
  banktransaktionId?: string): Promise<IAffärshändelseRespons> {

  const body = {
    affärshändelse,
    skallBokföras,
    banktransaktionId
  };

  return axiosInstance.post(endpoints.bokföring.affärshändelser.spara(orgId), body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}

export function SparaAffärshändelser(
  orgId: string,
  requests: { affärshändelse: IAffärshändelse; skallBokföras: boolean; banktransaktionId?: string }[]): Promise<IAffärshändelse[]> {

  return axiosInstance.post(endpoints.bokföring.affärshändelser.sparaBatch(orgId), requests, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error('Batch save error:', error);
      throw error;
    });
}


export interface IDuplikatAffärshändelse {
  id: string;
  beskrivning: string;
  inträffade: string;
  bokföringsDatum: string;
  totaltBelopp: number;
  bokförd: boolean;
  verifikationsNummer?: number;
}

export interface IDuplikatVarning {
  kandidatId: string;
  duplikat: IDuplikatAffärshändelse[];
}

/**
 * Frågar backend om någon av affärshändelserna redan finns sparad med samma dag, belopp och
 * bokföringskonton. Returnerar bara de kandidater som har träffar.
 */
export function kontrolleraDuplikat(
  orgId: string,
  affärshändelser: IAffärshändelse[]): Promise<IDuplikatVarning[]> {

  return axiosInstance.post(endpoints.bokföring.affärshändelser.duplikatkontroll(orgId), affärshändelser, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => response.data);
}


export function vändVerifikationAnrop(
  affärshändelseId: string,
  verifikationsId: string,
  orgId: string): Promise<IAffärshändelseRespons> {
  

  const body = {
    orgId,
    affärshändelseId,
    verifikationsId
  };

  return axiosInstance.post(endpoints.bokföring.bokfor.verifikation.vänd, body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}

export function bokforArbetsgivardeklaration(arbetsgivardeklaration: IArbetsgivardeklaration, orgId: string): Promise<IAffärshändelseRespons> {
  

  const body = {
    orgId,
    arbetsgivardeklaration
  };

  return axiosInstance.post(endpoints.bokföring.bokfor.arbetsgivardeklaration, body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}

export function hämtaBokföringAvSparkonton(orgId: string): Promise<IBokföringAvSparkontoRespons> {

  return axiosInstance.get(endpoints.bank.bokföringSparkonton.hämta(orgId))
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}


export function SlutförBokföringAvTransaktion(
  bokforingAvTransaktion: IBokföringAvTransaktion,
  valdBanktransaktion: IBanktransaktion | undefined,
  orgId: string): Promise<IAffärshändelseRespons> {

  const body = {
    orgId,
    bokforingAvTransaktion
  };

  return axiosInstance.post(endpoints.bank.bokföringSparkonton.hämta(orgId), body, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      return response.data
    })
    .catch(error => {
      console.error(error);
    });
}

export function hämtaAffärshändelseKandidater(
  affärshändelseId: string,
  orgId: string
): Promise<IAffärshändelse[]> {
  return axiosInstance
    .get(
      endpoints.bokföring.affärshändelser.hämta(orgId, affärshändelseId) +
        '/kandidater-för-flytt'
    )
    .then((response) => response.data)
    .catch((error) => {
      console.error('Error fetching candidates:', error);
      throw error;
    });
}

export function flyttaVerifikationer(
  sourceAffärshändelseId: string,
  destinationAffärshändelseId: string,
  verifikationIds: string[],
  orgId: string
): Promise<IAffärshändelse> {
  const body = {
    sourceAffärshändelseId,
    destinationAffärshändelseId,
    verifikationIds,
  };

  return axiosInstance
    .post(
      endpoints.bokföring.affärshändelser.hämtaAllaFörOrg(orgId) + '/flytta-verifikationer',
      body
    )
    .then((response) => response.data)
    .catch((error) => {
      console.error('Error moving verifikationer:', error);
      throw error;
    });
}

export function läggTillVerifikation(
  affärshändelseId: string,
  orgId: string,
  verifikationData: any
): Promise<IAffärshändelse> {
  return axiosInstance
    .post(
      endpoints.bokföring.affärshändelser.läggTillVerifikation(orgId, affärshändelseId),
      verifikationData
    )
    .then((response) => response.data)
    .catch((error) => {
      console.error('Error adding verifikation:', error);
      throw error;
    });
}

export type IVerifikationsTyp = {
  key: string;
  label: string;
};

export function hämtaVerifikationsTyper(): Promise<IVerifikationsTyp[]> {
  return axiosInstance
    .get(endpoints.bokföring.verifikationstyper)
    .then((response) => response.data)
    .catch((error) => {
      console.error('Error fetching verification types:', error);
      throw error;
    });
}
